"use client";

import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import { cn } from "@/lib/utils";

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn("grid w-full gap-2", className)}
      {...props}
    />
  );
}

function RadioGroupItem({
  className,
  style,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        "group/radio-group-item peer relative flex aspect-square size-4 shrink-0 rounded-full bg-transparent outline-none after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:ring-3 focus-visible:ring-tertiary-400 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-transparent",
        className,
      )}
      style={{
        border: "1.5px solid var(--color-input)",
        ...style,
      }}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="absolute -inset-0.5 flex items-center justify-center rounded-full"
        style={CHECKED_RING_STYLE}
      >
        <RadioGroupDot />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

const CHECKED_RING_STYLE: React.CSSProperties = {
  background:
    "linear-gradient(to bottom right, var(--color-primary-100), var(--color-secondary-100)) padding-box, linear-gradient(to bottom right, var(--color-primary-400), var(--color-secondary-400)) border-box",
  border: "1.5px solid transparent",
};

function RadioGroupDot() {
  return (
    <span
      className="size-2 rounded-full"
      style={{
        background:
          "linear-gradient(to bottom right, var(--color-primary-400), var(--color-secondary-400))",
      }}
    />
  );
}

function RadioIndicator({
  checked,
  className,
  style,
  ...props
}: React.ComponentProps<"span"> & { checked: boolean }) {
  return (
    <span
      data-slot="radio-indicator"
      data-state={checked ? "checked" : "unchecked"}
      className={cn(
        "flex aspect-square size-4 shrink-0 items-center justify-center rounded-full bg-white",
        className,
      )}
      style={{
        ...(checked
          ? CHECKED_RING_STYLE
          : { border: "1.5px solid var(--color-input)" }),
        ...style,
      }}
      {...props}
    >
      {checked && <RadioGroupDot />}
    </span>
  );
}

export {
  RadioGroup,
  RadioGroupItem,
  RadioIndicator,
  RadioGroupDot,
  CHECKED_RING_STYLE,
};
